import { Box as ChakraBox } from '@chakra-ui/react';
import type { ActivityMenuItem } from './activityMenuItem';

const Box = ChakraBox as any;

interface ActivityMenuItemIconProps {
  icon: NonNullable<ActivityMenuItem['icon']>;
  /**
   * Kept so the icon can hold the same line as its label. It paints nothing extra:
   * a destructive item is not red — see `activityMenuItem`.
   */
  destructive?: boolean;
  /** Chakra colour token; see `ActivityMenuItem.color`. */
  color?: string;
}

/**
 * A menu item's icon, in the item's own colour.
 *
 * kula's `Menu` paints every icon `secondary-400` (#A6A7D2) while painting the label
 * `secondary-700` (#6D659D) — so the icon reads a full step lighter than the word
 * next to it, and a five-item menu turns into a grey wash.
 *
 * The span carrying that colour is internal to the library, so the only way in is to
 * colour the icon itself — kula icons draw with `currentColor`, so an explicit
 * `color` on a wrapper wins over the inherited one. That is also what keeps a
 * destructive item's ⊗ out of kula's red: the library reddens `destructive` items on
 * its own, and legacy reds nothing.
 *
 * Lives in its own file because a module that exports a component must export ONLY
 * components, or Fast Refresh silently stops working for it
 * (`react-refresh/only-export-components`) — the same rule that moved `menuItemLabel`
 * out of `ActivityMoreMenu`.
 */
export const ActivityMenuItemIcon = ({
  icon: Icon,
  color,
}: ActivityMenuItemIconProps) => (
  <Box
    as="span"
    display="flex"
    color={color ?? 'secondary-700'}
    // On hover kula darkens the LABEL to `secondary-900` (Radix sets
    // `data-highlighted` on the item) but leaves the icon at its rest colour, so
    // the icon lags a step lighter than the word beside it. Follow the label off
    // the same signal — `[data-highlighted] &` matches this span under a
    // highlighted item.
    sx={{ '[data-highlighted] &': { color: color ?? 'secondary-900' } }}
    data-test-id="activity-menu-item-icon"
  >
    <Icon size={18} />
  </Box>
);
